import { FC, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { Category } from '@/__generated_graphql__/graphql';
import { Button } from '@/components/ui/Button/Button';
import { Checkbox } from '@/components/ui/Checkbox/Checkbox';
import { RightArrowIcon } from '@/components/ui/Icons/Icons';
import { useCategoriesFilter } from '@/hooks/useCategoriesFilter';

import * as S from './CategoriesFilterMobile.styles';

type CategoriesFilterMobileProps = {
  setActiveCategory: (category: Category) => void;
  activeCategory: Category | null | undefined;
};

export const CategoriesFilterMobile: FC<CategoriesFilterMobileProps> = ({
  setActiveCategory,
  activeCategory
}) => {
  const { t } = useTranslation();
  const { categories, watch, getCategoryById, toggleJob, discardJobs } =
    useCategoriesFilter();

  const selectedCategories = watch('categories');

  if (!categories) return null;

  const activeSubCategory = getCategoryById(activeCategory?.id);

  const handleReset = (e: MouseEvent<Element>, jobs: Category['children']) => {
    e.stopPropagation();
    discardJobs(jobs, selectedCategories);
  };

  const handleToggleJob = (job: Category) => toggleJob(job, selectedCategories);

  return (
    <S.Wrapper data-type="categories-filter">
      {activeCategory
        ? activeSubCategory?.children?.map(job => {
            return (
              <S.CategoryHeader
                data-type="job"
                data-testid="job-header"
                key={job.id}
              >
                <S.CategoryLabel $fullWidth>{job.name}</S.CategoryLabel>
                <Checkbox
                  isChecked={selectedCategories.some(selected => {
                    const isMainCategorySelected = selected.parentId === null;
                    return isMainCategorySelected
                      ? String(selected.id) === String(job.parentId)
                      : selected.id === job.id;
                  })}
                  onChange={() => handleToggleJob(job)}
                />
              </S.CategoryHeader>
            );
          })
        : categories.map(category => {
            const jobs = category.children;
            const someSelected = jobs.some(job =>
              selectedCategories.some(selected => {
                const isMainCategorySelected = selected.parentId === null;
                return isMainCategorySelected
                  ? category.id === selected.id
                  : selected.id === job.id;
              })
            );

            return (
              <S.CategoryWrapper key={category.id} data-type="category">
                <S.CategoryHeader
                  onClick={() => setActiveCategory(category)}
                  data-testid="category-header"
                >
                  <S.LabelWrapper>
                    <S.CategoryLabel>{category.name}</S.CategoryLabel>
                    {someSelected && (
                      <Button
                        style={{ all: 'unset' }}
                        onClick={e => handleReset(e, [...jobs, category])}
                      >
                        <S.DiscardLabel>
                          {t('Filter.reset_button')}
                        </S.DiscardLabel>
                      </Button>
                    )}
                  </S.LabelWrapper>
                  <S.Arrow>
                    <RightArrowIcon />
                  </S.Arrow>
                </S.CategoryHeader>
              </S.CategoryWrapper>
            );
          })}
    </S.Wrapper>
  );
};
